import "./Links.scss";
import { useState, useRef } from "react";
import {
  Dotpoints02,
  Link01,
  LinkExternal01,
  Mail02,
  PlusCircle,
  Trash01,
} from "@untitled-ui/icons-react";
import { validateAndDetermineType } from "../../utils/linkValidator";
import { fetcher } from "../../utils/fetcher";
import ToolTip from "../ToolTip/ToolTip";
import Modal from "../Modal/Modal";
import LinkModal from "./LinkModal/LinkModal";
import LinkExistingNoteModal from "./LinkModal/LinkExistingNoteModal";
import LinkExistingListModal from "./LinkModal/LinkExistingListModal";
import NoteEditorModal from "./LinkModal/NoteEditorModal";
import ListEditorModal from "./LinkModal/ListEditorModal";
import CreateNewListModal from "./LinkModal/CreateNewListModal";
import CreateNewNoteModal from "./LinkModal/CreateNewNoteModal";
import AddLinkFlow from "./AddLinkFlow";
import { useTranslation } from "react-i18next";

// Copy text to the clipboard. Falls back to a hidden textarea where the
// Clipboard API isn't available (e.g. a non-HTTPS address).
const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

function Links({ taskData, onTaskUpdate }) {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  // Link whose copy button shows a checkmark for a moment after copying
  const [copiedLinkKey, setCopiedLinkKey] = useState(null);
  const [infoText, setInfoText] = useState(""); // Add info text state
  const [infoType, setInfoType] = useState(""); // Add info type state (success, error, warning)

  const toolTipRef = useRef();
  const modalRef = useRef();

  const showToolTip = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (toolTipRef.current) {
      toolTipRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const openModal = (e, arrow, content) => {
    const triggerElement = e?.currentTarget ?? null;
    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const handleCopyLink = async (e, key, text) => {
    e.stopPropagation();
    try {
      await copyToClipboard(text);
      setCopiedLinkKey(key);
      setTimeout(
        () => setCopiedLinkKey((current) => (current === key ? null : current)),
        1500,
      );
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  // If no task data is provided, return early
  if (!taskData) {
    return (
      <div className="links">
        <div className="links-wrapper">
          <div className="link placeholder">
            <div className="name">{t("tasks.images.noTaskSelected")}</div>
          </div>
        </div>
      </div>
    );
  }

  // Separate links, emails, linked notes, and linked lists from task data
  const links = taskData?.links?.filter((link) => link.type === "link") || [];
  const emails = taskData?.links?.filter((link) => link.type === "email") || [];
  const linkedNotes =
    taskData?.links?.filter((link) => link.type === "note") || [];
  const linkedLists =
    taskData?.links?.filter((link) => link.type === "list") || [];

  // Handle linking a list to the task
  const handleLinkList = async (list) => {
    try {
      setIsUpdating(true);

      // Check if list is already linked
      const isAlreadyLinked = taskData.links?.some(
        (link) => link.type === "list" && link.listId === list._id,
      );

      if (isAlreadyLinked) {
        setInfoText(t("tasks.links.listAlreadyLinked"));
        setInfoType("warning");
        setTimeout(() => {
          setInfoText("");
          setInfoType("");
        }, 3000);
        return;
      }

      const newLink = {
        name: list.title,
        url: `/lists/${list._id}`, // Internal reference
        type: "list",
        listId: list._id,
        listType: list.type,
        listColor: list.color,
      };

      const updatedLinks = [...(taskData.links || []), newLink];
      const updatedTaskData = {
        ...taskData,
        links: updatedLinks,
      };

      const savedTask = await fetcher.put(
        `/api/tasks/${taskData._id}`,
        updatedTaskData,
      );

      setInfoText(t("tasks.links.listLinked"));
      setInfoType("success");
      setTimeout(() => {
        setInfoText("");
        setInfoType("");
      }, 3000);

      if (onTaskUpdate) {
        onTaskUpdate(savedTask);
      }
    } catch (error) {
      console.error("Error linking list:", error);
      setInfoText(t("tasks.links.linkListFailed"));
      setInfoType("error");
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle linking a note to the task
  const handleLinkNote = async (note, group) => {
    try {
      setIsUpdating(true);

      // Check if note is already linked
      const isAlreadyLinked = taskData.links?.some(
        (link) => link.type === "note" && link.noteId === note._id,
      );

      if (isAlreadyLinked) {
        setInfoText(t("tasks.links.noteAlreadyLinked"));
        setInfoType("warning");
        setTimeout(() => {
          setInfoText("");
          setInfoType("");
        }, 3000);
        return;
      }

      const newLink = {
        name: note.title,
        url: `/notes/${note._id}`, // Internal reference
        type: "note",
        noteId: note._id,
        noteGroupId: group._id,
        noteGroupName: group.name,
        noteGroupColor: group.color,
      };

      const updatedLinks = [...(taskData.links || []), newLink];
      const updatedTaskData = {
        ...taskData,
        links: updatedLinks,
      };

      const savedTask = await fetcher.put(
        `/api/tasks/${taskData._id}`,
        updatedTaskData,
      );

      setInfoText(t("tasks.links.noteLinked"));
      setInfoType("success");
      setTimeout(() => {
        setInfoText("");
        setInfoType("");
      }, 3000);

      if (onTaskUpdate) {
        onTaskUpdate(savedTask);
      }
    } catch (error) {
      console.error("Error linking note:", error);
      setInfoText(t("tasks.links.linkNoteFailed"));
      setInfoType("error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddLink = async (name, url, setInfo) => {
    if (!url.trim()) {
      setInfo(t("tasks.links.urlRequired"), "error");
      return;
    }

    const validation = validateAndDetermineType(url);

    if (!validation.isValid) {
      setInfo(t("tasks.links.urlInvalid"), "error");
      return;
    }

    const newLink = {
      name: name.trim() || (validation.type === "email" ? url : ""),
      url: validation.url,
      type: validation.type,
    };

    try {
      setIsUpdating(true);
      const updatedLinks = [...(taskData.links || []), newLink];
      const updatedTaskData = {
        ...taskData,
        links: updatedLinks,
      };

      // Save to database and get the updated task with proper _id values
      const savedTask = await fetcher.put(
        `/api/tasks/${taskData._id}`,
        updatedTaskData,
      );

      // Notify parent component with the saved task data (includes MongoDB _id values)
      if (onTaskUpdate) {
        onTaskUpdate(savedTask);
      }

      // Close modal on success
      closeModal();
    } catch (error) {
      console.error("Error adding link:", error);
      setInfo(t("tasks.links.addFailed"), "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      setIsUpdating(true);
      const updatedLinks = taskData.links.filter((link) => link._id !== linkId);
      const updatedTaskData = {
        ...taskData,
        links: updatedLinks,
      };

      // Save to database and get the updated task
      const savedTask = await fetcher.put(
        `/api/tasks/${taskData._id}`,
        updatedTaskData,
      );

      // Notify parent component with the saved task data
      if (onTaskUpdate) {
        onTaskUpdate(savedTask);
      }
    } catch (error) {
      console.error("Error removing link:", error);
      setInfoText(t("tasks.links.removeFailed"));
      setInfoType("error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenLink = (url) => {
    if (url.startsWith("mailto:")) {
      // For email addresses, copy to clipboard
      const email = url.replace("mailto:", "");
      navigator.clipboard
        .writeText(email)
        .then(() => {
          setInfoText(t("tasks.links.emailCopied"));
          setInfoType("success");
          // Clear the message after 3 seconds
          setTimeout(() => {
            setInfoText("");
            setInfoType("");
          }, 3000);
        })
        .catch((err) => {
          console.error("Failed to copy email to clipboard:", err);
          setInfoText(t("tasks.links.emailCopyFailed"));
          setInfoType("error");
          setTimeout(() => {
            setInfoText("");
            setInfoType("");
          }, 3000);
        });
    } else {
      // For regular links, open in new tab
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // Opens (or re-opens) the AddLink flow, optionally at a specific screen
  const openAddLinkFlow = (initialScreen = "type") => {
    closeModal();
    setTimeout(() => {
      openModal(
        null,
        "center-center",
        <LinkModal
          title={t("tasks.links.addLink")}
          onClose={closeModal}
          size="medium"
        >
          <AddLinkFlow
            initialScreen={initialScreen}
            onSelectList={(action) => {
              closeModal();
              setTimeout(() => {
                if (action === "create") {
                  openModal(
                    null,
                    "center-center",
                    <LinkModal
                      title={t("tasks.links.createNewList")}
                      onClose={closeModal}
                      size="small"
                    >
                      <button
                        className="back-btn"
                        onClick={() => openAddLinkFlow("listChoice")}
                      >
                        <span className="material-symbols-outlined">
                          arrow_back
                        </span>
                        {t("common.back")}
                      </button>
                      <CreateNewListModal
                        onClose={closeModal}
                        onListCreated={async (newList) => {
                          await handleLinkList(newList);
                          closeModal();
                          setTimeout(() => {
                            if (modalRef.current) {
                              modalRef.current.open({
                                arrow: "center-center",
                                content: (
                                  <LinkModal
                                    title={newList.title}
                                    onClose={closeModal}
                                  >
                                    <ListEditorModal
                                      listId={newList._id}
                                      onListUpdated={(updatedList) => {
                                        if (
                                          updatedList.title !== newList.title
                                        ) {
                                          const updatedLinks =
                                            taskData.links.map((l) =>
                                              l.listId === newList._id
                                                ? {
                                                    ...l,
                                                    name: updatedList.title,
                                                  }
                                                : l,
                                            );
                                          onTaskUpdate({
                                            ...taskData,
                                            links: updatedLinks,
                                          });
                                        }
                                      }}
                                    />
                                  </LinkModal>
                                ),
                                triggerElement: null,
                              });
                            }
                          }, 100);
                        }}
                      />
                    </LinkModal>,
                  );
                } else {
                  openModal(
                    null,
                    "center-center",
                    <LinkModal
                      title={t("tasks.links.linkExistingList")}
                      onClose={closeModal}
                    >
                      <button
                        className="back-btn"
                        onClick={() => openAddLinkFlow("listChoice")}
                      >
                        <span className="material-symbols-outlined">
                          arrow_back
                        </span>
                        {t("common.back")}
                      </button>
                      <LinkExistingListModal
                        onSelect={(list) => handleLinkList(list)}
                        onClose={closeModal}
                      />
                    </LinkModal>,
                  );
                }
              }, 100);
            }}
            onSelectNote={(action) => {
              closeModal();
              setTimeout(() => {
                if (action === "create") {
                  openModal(
                    null,
                    "center-center",
                    <LinkModal
                      title={t("tasks.links.createNewNote")}
                      onClose={closeModal}
                      size="small"
                    >
                      <button
                        className="back-btn"
                        onClick={() => openAddLinkFlow("noteChoice")}
                      >
                        <span className="material-symbols-outlined">
                          arrow_back
                        </span>
                        {t("common.back")}
                      </button>
                      <CreateNewNoteModal
                        onClose={closeModal}
                        onNoteCreated={async (newNote, group) => {
                          await handleLinkNote(newNote, group);
                          closeModal();
                          setTimeout(() => {
                            if (modalRef.current) {
                              modalRef.current.open({
                                arrow: "center-center",
                                content: (
                                  <LinkModal
                                    title={newNote.title}
                                    onClose={closeModal}
                                    size="large"
                                  >
                                    <NoteEditorModal
                                      noteId={newNote._id}
                                      onNoteUpdated={(updatedNote) => {
                                        if (
                                          updatedNote.title !== newNote.title
                                        ) {
                                          const updatedLinks =
                                            taskData.links.map((l) =>
                                              l.noteId === newNote._id
                                                ? {
                                                    ...l,
                                                    name: updatedNote.title,
                                                  }
                                                : l,
                                            );
                                          onTaskUpdate({
                                            ...taskData,
                                            links: updatedLinks,
                                          });
                                        }
                                      }}
                                    />
                                  </LinkModal>
                                ),
                                triggerElement: null,
                              });
                            }
                          }, 100);
                        }}
                      />
                    </LinkModal>,
                  );
                } else {
                  openModal(
                    null,
                    "center-center",
                    <LinkModal
                      title={t("tasks.links.linkExistingNote")}
                      onClose={closeModal}
                    >
                      <button
                        className="back-btn"
                        onClick={() => openAddLinkFlow("noteChoice")}
                      >
                        <span className="material-symbols-outlined">
                          arrow_back
                        </span>
                        {t("common.back")}
                      </button>
                      <LinkExistingNoteModal
                        onSelect={(note, group) => handleLinkNote(note, group)}
                        onClose={closeModal}
                      />
                    </LinkModal>,
                  );
                }
              }, 100);
            }}
            onAddExternalLink={handleAddLink}
            onClose={closeModal}
            isUpdating={isUpdating}
          />
        </LinkModal>,
      );
    }, 100);
  };

  return (
    <>
      <ToolTip ref={toolTipRef} />
      <Modal ref={modalRef} />
      <div className="links">
        {/* Add Link Button at the top */}
        <button
          className="add-link-main-button"
          onClick={() => openAddLinkFlow("type")}
        >
          <span className="material-symbols-outlined">add</span>
          <span>{t("tasks.links.addLink")}</span>
        </button>

        {infoText && (
          <div className={`links-toast ${infoType}`}>
            <span className="material-symbols-outlined">
              {infoType === "success"
                ? "check_circle"
                : infoType === "error"
                  ? "error"
                  : "info"}
            </span>
            {infoText}
          </div>
        )}

        <div className="links-wrapper">
          {/* Link List Section */}
          {linkedLists.length > 0 && (
            <div className="links-section">
              <div className="section-header">
                <div className="section-title">
                  <span className="material-symbols-outlined section-icon">
                    list
                  </span>
                  <span className="section-text">
                    {t("tasks.links.linkedLists")}
                  </span>
                </div>
              </div>
              <div className="links-table linked-lists-table">
                {linkedLists.length > 0 ? (
                  linkedLists.map((list) => (
                    <div
                      key={list._id}
                      className="link-card"
                      onClick={(e) =>
                        openModal(
                          e,
                          "center-center",
                          <LinkModal title={list.name} onClose={closeModal}>
                            <ListEditorModal
                              listId={list.listId}
                              onListUpdated={(updatedList) => {
                                if (updatedList.title !== list.name) {
                                  const updatedLinks = taskData.links.map(
                                    (l) =>
                                      l._id === list._id
                                        ? { ...l, name: updatedList.title }
                                        : l,
                                  );
                                  onTaskUpdate({
                                    ...taskData,
                                    links: updatedLinks,
                                  });
                                }
                              }}
                            />
                          </LinkModal>,
                        )
                      }
                    >
                      <div className="link-icon">
                        <span
                          className="material-symbols-outlined"
                          style={{ color: list.listColor }}
                        >
                          {list.listType === "checklist" ? "checklist" : "list"}
                        </span>
                      </div>
                      <div className="link-content">
                        <div className="link-name">{list.name}</div>
                        <div className="link-meta">
                          {list.listType === "checklist"
                            ? t("tasks.links.checklist")
                            : list.listType === "todo"
                              ? t("tasks.links.todoList")
                              : list.listType}
                        </div>
                      </div>
                      <div className="link-actions">
                        <button
                          className="link-action-btn danger"
                          onMouseEnter={(e) =>
                            showToolTip(
                              e,
                              "top-right",
                              t("tasks.links.unlinkList"),
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLink(list._id);
                          }}
                        >
                          <span className="material-symbols-outlined">
                            link_off
                          </span>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <span className="material-symbols-outlined empty-icon">
                      list
                    </span>
                    <p>{t("tasks.links.noLinkedLists")}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Link Note Section */}
          {linkedNotes.length > 0 && (
            <div className="links-section">
              <div className="section-header">
                <div className="section-title">
                  <span className="material-symbols-outlined section-icon">
                    note
                  </span>
                  <span className="section-text">
                    {t("tasks.links.linkedNotes")}
                  </span>
                </div>
              </div>
              <div className="links-table linked-notes-table">
                {linkedNotes.length > 0 ? (
                  linkedNotes.map((note) => (
                    <div
                      key={note._id}
                      className="link-card"
                      onClick={(e) =>
                        openModal(
                          e,
                          "center-center",
                          <LinkModal
                            title={note.name}
                            onClose={closeModal}
                            size="large"
                          >
                            <NoteEditorModal
                              noteId={note.noteId}
                              onNoteUpdated={(updatedNote) => {
                                if (updatedNote.title !== note.name) {
                                  const updatedLinks = taskData.links.map(
                                    (l) =>
                                      l._id === note._id
                                        ? { ...l, name: updatedNote.title }
                                        : l,
                                  );
                                  onTaskUpdate({
                                    ...taskData,
                                    links: updatedLinks,
                                  });
                                }
                              }}
                            />
                          </LinkModal>,
                        )
                      }
                    >
                      <div className="link-icon">
                        <span
                          className="material-symbols-outlined"
                          style={{ color: note.noteGroupColor || "inherit" }}
                        >
                          description
                        </span>
                      </div>
                      <div className="link-content">
                        <div className="link-name">{note.name}</div>
                        <div className="link-meta">{note.noteGroupName}</div>
                      </div>
                      <div className="link-actions">
                        <button
                          className="link-action-btn danger"
                          onMouseEnter={(e) =>
                            showToolTip(
                              e,
                              "top-right",
                              t("tasks.links.unlinkNote"),
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLink(note._id);
                          }}
                        >
                          <span className="material-symbols-outlined">
                            link_off
                          </span>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <span className="material-symbols-outlined empty-icon">
                      note
                    </span>
                    <p>{t("tasks.links.noLinkedNotes")}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* External Links Section */}
          {links.length > 0 && (
            <div className="links-section">
              <div className="section-header">
                <div className="section-title">
                  <span className="material-symbols-outlined section-icon">
                    link
                  </span>
                  <span className="section-text">
                    {t("tasks.links.externalLinks")}
                  </span>
                </div>
              </div>
              <div className="links-table">
                {links.length > 0 ? (
                  links.map((link, index) => (
                    <div
                      key={link._id || `link-${index}-${link.url}`}
                      className="link-card"
                      onClick={() => handleOpenLink(link.url)}
                    >
                      <div className="link-icon">
                        <span className="material-symbols-outlined">link</span>
                      </div>
                      <div className="link-content">
                        <div className="link-name">
                          {link.name || t("tasks.links.unnamedLink")}
                        </div>
                        <div className="link-meta url-text">{link.url}</div>
                      </div>
                      <div className="link-actions">
                        <button
                          className="link-action-btn"
                          onMouseEnter={(e) =>
                            showToolTip(
                              e,
                              "top-right",
                              t("tasks.links.copyLink"),
                            )
                          }
                          onClick={(e) =>
                            handleCopyLink(e, link._id || link.url, link.url)
                          }
                        >
                          <span className="material-symbols-outlined">
                            {copiedLinkKey === (link._id || link.url)
                              ? "check"
                              : "content_copy"}
                          </span>
                        </button>
                        <button
                          className="link-action-btn danger"
                          onMouseEnter={(e) =>
                            showToolTip(
                              e,
                              "top-right",
                              t("tasks.links.removeLink"),
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLink(link._id);
                          }}
                        >
                          <span className="material-symbols-outlined">
                            delete
                          </span>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <span className="material-symbols-outlined empty-icon">
                      link
                    </span>
                    <p>{t("tasks.links.noExternalLinks")}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Emails Section */}
          {emails.length > 0 && (
            <div className="links-section">
              <div className="section-header">
                <div className="section-title">
                  <span className="material-symbols-outlined section-icon">
                    mail
                  </span>
                  <span className="section-text">
                    {t("tasks.links.emailAddresses")}
                  </span>
                </div>
              </div>
              <div className="links-table">
                {emails.length > 0 ? (
                  emails.map((email, index) => (
                    <div
                      key={email._id || `email-${index}-${email.url}`}
                      className="link-card"
                      onClick={() => handleOpenLink(email.url)}
                    >
                      <div className="link-icon">
                        <span className="material-symbols-outlined">mail</span>
                      </div>
                      <div className="link-content">
                        <div className="link-name">
                          {email.name || t("tasks.links.unnamedContact")}
                        </div>
                        <div className="link-meta email-text">
                          {email.url.replace("mailto:", "")}
                        </div>
                      </div>
                      <div className="link-actions">
                        <button
                          className="link-action-btn"
                          onMouseEnter={(e) =>
                            showToolTip(
                              e,
                              "top-right",
                              t("tasks.links.copyEmail"),
                            )
                          }
                          onClick={(e) =>
                            handleCopyLink(
                              e,
                              email._id || email.url,
                              email.url.replace("mailto:", ""),
                            )
                          }
                        >
                          <span className="material-symbols-outlined">
                            {copiedLinkKey === (email._id || email.url)
                              ? "check"
                              : "content_copy"}
                          </span>
                        </button>
                        <button
                          className="link-action-btn danger"
                          onMouseEnter={(e) =>
                            showToolTip(
                              e,
                              "top-right",
                              t("tasks.links.removeEmail"),
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLink(email._id);
                          }}
                        >
                          <span className="material-symbols-outlined">
                            delete
                          </span>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <span className="material-symbols-outlined empty-icon">
                      mail
                    </span>
                    <p>{t("tasks.links.noEmailAddresses")}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Links;
